import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { TableCellWithWidth } from "components/list/TableComponentsStyled";
import { Table, TableBody, TableHead, TableRow } from "@mui/material";
import { useQueryWithSimpleErrorHandling } from 'hooks/apolloWrappers';
import { useMutation } from "@apollo/client";
import { BALANCE_SHEET_DETAILS_BY_ACCOUNT_CLASS } from "../queries";
import { SAVE_PARTNER_BALANCE_SHEET_DETAIL } from "../mutations";
import { BalanceSheetDetailsListRowComponent } from "./BalanceSheetDetailsListRowComponent";
import { FakeText } from "@totalsoft/rocket-ui";
import { update, assoc } from "ramda";
import { useSimpleError } from "hooks/apolloWrappers";

export const BalanceSheetDetailsListContainer = ({ balanceSheetId, accountClass, isExpanded, currencyCode }) => {
  const [t] = useTranslation();
  const [balanceSheetDetails, setBalanceSheetDetails] = useState([]);
  const showError = useSimpleError();

  const { loading } = useQueryWithSimpleErrorHandling(BALANCE_SHEET_DETAILS_BY_ACCOUNT_CLASS, {
    onCompleted: data => setBalanceSheetDetails(data?.partnerBalanceSheetDetailsByAccountClass || []),
    variables: { balanceSheetId: balanceSheetId, accountClass: accountClass },
    skip: !isExpanded
  });

  const [savePartnerBalanceSheetDetails] = useMutation(SAVE_PARTNER_BALANCE_SHEET_DETAIL, {
    onError: error => {
      showError(error);
    },
  });

  const handleSave = useCallback(
    detail => {
      savePartnerBalanceSheetDetails({
        variables: {
          input: {
            balanceSheetDetailId: detail.balanceSheetDetailId,
            balanceSheetId: detail.balanceSheetId,
            configDetailId: detail.configDetailId,
            creditCurrencyAmount: detail.creditCurrencyAmount,
            debitCurrencyAmount: detail.debitCurrencyAmount,
            eqCreditAmount: detail.eqCreditAmount,
            eqDebitAmount: detail.eqDebitAmount
          }
        }
      });
    },
    [savePartnerBalanceSheetDetails]
  );

  const handleChange = useCallback((prop, value, index) => {
      setBalanceSheetDetails(prev => update(index, assoc(prop, value, prev[index]), prev));
  }, []);

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCellWithWidth w={30}>{t("BalanceSheet.Details.AccountName")}</TableCellWithWidth>
          <TableCellWithWidth w={15}>{t("BalanceSheet.Details.AccountSymbol")}</TableCellWithWidth>
          <TableCellWithWidth w={15}>{t("BalanceSheet.Details.AccountType")}</TableCellWithWidth>
          <TableCellWithWidth style={{ align: 'right'}} w={20}><>{t("BalanceSheet.Details.Debit")} ({currencyCode})</></TableCellWithWidth>
          <TableCellWithWidth style={{ align: 'right'}} w={20}><>{t("BalanceSheet.Details.Credit")} ({currencyCode})</></TableCellWithWidth>          
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          <FakeText lines={10} />
        ) : (
          balanceSheetDetails.map((item, index) => (
            <BalanceSheetDetailsListRowComponent
              key={item.configDetailId}
              item={item}
              index={index}
              handleChange={handleChange}
              handleSave={handleSave}
            />
          ))
        )}
      </TableBody>
    </Table>
  );
};

BalanceSheetDetailsListContainer.propTypes = {
  balanceSheetId: PropTypes.number.isRequired,
  accountClass: PropTypes.string.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  currencyCode: PropTypes.string
};

export default React.memo(BalanceSheetDetailsListContainer);
